import { ref } from 'vue'
import { logger } from '@siclop/kstone-vue'
import { CompanyService } from '../services/CompanyService'

/**
 * Compteurs « Projets » et « Documents » de l'écran Mon entreprise (PC et mobile).
 *
 * Toute l'activité de l'entreprise, chargée dès l'ouverture (les onglets sont paresseux : leurs
 * `count-change` n'arrivent qu'à la première visite) par une route de comptage :
 * - projets : tous les projets visibles (pas seulement ceux dont l'entreprise est cliente) ;
 * - documents : tout l'explorateur « Documents de l'entreprise », sous-dossiers compris.
 *
 * L'onglet Projets liste les mêmes projets : `setProjects(n)` aligne le compteur sur la liste reçue.
 * L'explorateur ne compte que le dossier affiché : on recompte (`refresh`) après un dépôt, une
 * suppression ou une création de dossier plutôt que de reprendre son nombre.
 */
export function useCompanyActivityCounts() {
  const projectsCount = ref(0)
  const documentsCount = ref(0)
  const loading = ref(false)
  /** Erreur du dernier chargement (les compteurs gardent leur dernière valeur connue) */
  const failed = ref(false)

  async function refresh(): Promise<void> {
    loading.value = true
    try {
      const counts = await CompanyService.getActivityCounts()
      projectsCount.value = counts.projects
      documentsCount.value = counts.documents
      failed.value = false
    } catch (e: unknown) {
      // Compteurs d'information : un échec ne bloque pas l'écran (les onglets chargent leurs listes)
      failed.value = true
      logger.error('[useCompanyActivityCounts] Error loading company activity counts:', e)
    } finally {
      loading.value = false
    }
  }

  function setProjects(count: number): void {
    projectsCount.value = count
  }

  return { projectsCount, documentsCount, loading, failed, refresh, setProjects }
}
